import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { CloseOverviewEntityIcon } from "@common/schemas/clientsCloseOverview.schema";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { sortEntitiesByBusinessThenName } from "@components/entity/sortEntitiesByBusinessThenName";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { useGoToClientEntitySettings } from "@components/organizationView/clients/useGoToClientEntitySettings";
import { ClientsCloseOverviewRow } from "../clientsCloseOverviewViewConfig";

export const EntitiesCell = memo(
    ({
        getValue,
        row,
    }: CellContext<
        ViewTableFeatures,
        ClientsCloseOverviewRow,
        CloseOverviewEntityIcon[]
    >) => {
        const entities = getValue();
        const workspace = row.original;

        const sortedEntities = useMemo(
            () => sortEntitiesByBusinessThenName(entities ?? []),
            [entities],
        );

        const handleEntitySelected = useGoToClientEntitySettings();

        if (!sortedEntities || sortedEntities.length === 0) {
            return null;
        }

        // Workspace row IDs are prefixed with "workspace:" — strip to get the raw UUID
        const workspaceId = workspace.id.startsWith("workspace:")
            ? workspace.id.slice("workspace:".length)
            : workspace.id;

        return (
            <CaptureClicks>
                <EntityAvatarGroup
                    entities={sortedEntities}
                    max={5}
                    size="sm"
                    onEntityClick={(entity) =>
                        handleEntitySelected(workspaceId, entity.id)
                    }
                />
            </CaptureClicks>
        );
    },
);

EntitiesCell.displayName = "EntitiesCell";
